import React, { useEffect, useState } from 'react';
import './AiSocialAnalysisModal.css';
import { trackButtonClick, BUTTON_ENUMS } from './userJourneyTracker';

function normalizeHandle(value) {
  return String(value || '')
    .trim()
    .replace(/^@+/, '');
}

export default function AiSocialAnalysisModal({ isOpen, onClose, onAnalyse, isPremiumUser = false }) {
  const [instagramHandle, setInstagramHandle] = useState('');
  const [youtubeHandle, setYoutubeHandle] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setIsSubmitting(false);
      setInstagramHandle('');
      setYoutubeHandle('');
    }
  }, [isOpen]);

  const handleClose = () => onClose();

  const handleCta = async () => {
    const instagram = normalizeHandle(instagramHandle);
    const youtube = normalizeHandle(youtubeHandle);
    if (!instagram && !youtube) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (onAnalyse) {
        await onAnalyse({ instagramHandle: instagram, youtubeHandle: youtube });
      }
      trackButtonClick(BUTTON_ENUMS.AI_SOCIAL_ANALYSIS_SUBMITTED);
      setShowSuccess(true);
    } catch {
      // Stay on form if parent handler rejects; parent can surface its own error.
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasHandle =
    normalizeHandle(instagramHandle).length > 0 || normalizeHandle(youtubeHandle).length > 0;

  if (!isOpen) {
    return null;
  }

  const titleId = showSuccess
    ? 'ai-social-analysis-modal-success-title'
    : 'ai-social-analysis-modal-title';

  return (
    <div
      className="ai-social-analysis-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="ai-social-analysis-modal-backdrop"
        aria-label="Dismiss"
        onClick={handleClose}
      />
      <div className="ai-social-analysis-modal-dialog">
        <button
          type="button"
          className="ai-social-analysis-modal-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M18 6L6 18M6 6l12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {showSuccess ? (
          <div className="ai-social-analysis-modal-success">
            <div className="ai-social-analysis-modal-success-icon-wrap" aria-hidden="true">
              <div className="ai-social-analysis-modal-success-icon">
                <svg viewBox="0 0 24 24" width="36" height="36" fill="none">
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#ffffff"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <h2 id={titleId} className="ai-social-analysis-modal-success-title">
              Successfully submitted!
            </h2>
            <p className="ai-social-analysis-modal-success-sub">
              You&apos;ll get a notification once your analysis is complete.
            </p>

            <button type="button" className="ai-social-analysis-modal-cta" onClick={handleClose}>
              <span className="ai-social-analysis-modal-cta-label">Got it</span>
            </button>
          </div>
        ) : (
          <>
            <div className="ai-social-analysis-modal-hero">
              <span
                className="ai-social-analysis-modal-sparkle ai-social-analysis-modal-sparkle--1"
                aria-hidden="true"
              >
                ✦
              </span>
              <span
                className="ai-social-analysis-modal-sparkle ai-social-analysis-modal-sparkle--2"
                aria-hidden="true"
              >
                ✦
              </span>
              <span
                className="ai-social-analysis-modal-sparkle ai-social-analysis-modal-sparkle--3"
                aria-hidden="true"
              >
                ✦
              </span>
              <div className="ai-social-analysis-modal-icon-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
                  <path
                    d="M4 19V5M4 19h16M8 15l3-4 3 2 4-6"
                    stroke="url(#aam-chart-grad)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <defs>
                    <linearGradient id="aam-chart-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#5170ff" />
                      <stop offset="100%" stopColor="#ff66c4" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            <h2 id={titleId} className="ai-social-analysis-modal-title">
              Your next clip could be
              <br />
              <span className="ai-social-analysis-modal-title-accent">your BIGGEST one.</span>
            </h2>

            <p className="ai-social-analysis-modal-sub">
              Add your Instagram or YouTube handle and our AI analyses what&apos;s already working
              for your audience — so every clip is built to perform.
            </p>

            <div className="ai-social-analysis-modal-stat-block">
              <div className="ai-social-analysis-modal-stat">
                <div className="ai-social-analysis-modal-stat-top">
                  <span className="ai-social-analysis-modal-stat-value">3.2×</span>
                  <span className="ai-social-analysis-modal-stat-hero-label">more views</span>
                  <img
                    src={`${process.env.PUBLIC_URL}/stat_graph.png`}
                    alt=""
                    className="ai-social-analysis-modal-stat-graph"
                  />
                </div>
                <p className="ai-social-analysis-modal-stat-text">
                  Creators who optimise their clips for their audience see dramatically higher reach
                  in the first 24 hours.
                </p>
              </div>
              <p className="ai-social-analysis-modal-stat-source">
                Source: MakeReels internal data, 2025
              </p>

              <div className="ai-social-analysis-modal-handles">
                <h3 className="ai-social-analysis-modal-handles-heading">Your creator profiles</h3>

                <label className="ai-social-analysis-modal-url-field">
                  <span className="ai-social-analysis-modal-url-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="aam-insta-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#f58529" />
                          <stop offset="45%" stopColor="#dd2a7b" />
                          <stop offset="100%" stopColor="#8134af" />
                        </linearGradient>
                      </defs>
                      <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#aam-insta-grad)" strokeWidth="2" />
                      <circle cx="12" cy="12" r="4" stroke="url(#aam-insta-grad)" strokeWidth="2" />
                      <circle cx="17.5" cy="6.5" r="1.25" fill="url(#aam-insta-grad)" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="ai-social-analysis-modal-url-input"
                    placeholder="@yourhandle or profile URL"
                    value={instagramHandle}
                    onChange={(e) => setInstagramHandle(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    inputMode="text"
                  />
                </label>

                <label className="ai-social-analysis-modal-url-field">
                  <span className="ai-social-analysis-modal-url-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                        fill="#FF0000"
                      />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="ai-social-analysis-modal-url-input"
                    placeholder="@yourhandle or channel URL"
                    value={youtubeHandle}
                    onChange={(e) => setYoutubeHandle(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    inputMode="text"
                  />
                </label>
              </div>
            </div>

            {isPremiumUser ? (
              <button
                type="button"
                className="ai-social-analysis-modal-cta"
                onClick={handleCta}
                disabled={!hasHandle || isSubmitting}
              >
                <span className="ai-social-analysis-modal-cta-label">
                  {isSubmitting ? 'Submitting…' : 'Analyse my content'}
                </span>
                {!isSubmitting ? (
                  <svg
                    className="ai-social-analysis-modal-cta-chevron"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : null}
              </button>
            ) : (
              <p className="ai-social-analysis-modal-premium-required" role="status">
                Premium access required
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
